import {
  useMutation,
  useQueries,
  useQuery,
  useQueryClient,
  UseMutationResult,
  UseQueryResult,
} from "@tanstack/react-query";
import { toast } from "react-toastify";
import {
  addClient,
  editClient,
  deleteClient,
  getAllClients,
  getClientDetail,
  editGasMapping,
  createDevice,
  getAllDevices,
  getUserDevices,
  getDevicesRange,
  getDevicesForLocation,
  updateDevice,
  updateDeviceRange,
  deleteDevice,
  registerDevice,
  connectDeviceWithClient,
  changeDeviceState,
  createLocation,
  getAllLocations,
  getLocation,
  updateLocation,
  deleteLocation,
  getAllUsers,
  signUpUser,
  deleteUser,
  deactivateUser,
  getUserPreference,
  updatePreference,
  generateCredentials,
  updateUserRole,
  getUnassignedUsers,
  assignUserToClient,
  switchUserClient,
  updateProfile,
  getAvailableClients,
  onboardOrganization,
  getWeatherData,
  getLiveWeatherData,
  generateReport,
  getReports,
  downloadReport,
  deleteReport,
  sendEmail,
  createParameter,
  getDeviceParameters,
  updateParameter,
  deleteParameter,
  createComplianceProfile,
  getAllComplianceProfiles,
  updateComplianceProfile,
  deleteComplianceProfile,
  checkCompliance,
  downloadComplianceReport,
} from "../api/admin";
import { getMessage } from "../api/auth";
import { getMsFromTimeGap, TimeGap, TimePeriod } from "../utils/TimeConstants";
import { handleApiError } from "../queries/api";
import { ClientModel, ClientDetailModel, CustomResponse } from "../types/user";
import {
  IWeatherDataRange,
  RegisterDeviceDto,
  ConnectDeviceModel,
  ChangeDeviceModel,
  IPreference,
  IWeatherData,
  ReportRequestDto,
  ReportItem,
  SendEmailDto,
  ParameterCreateDto,
  ParameterUpdateDto,
} from "../types/device";
import { ICompanyModel } from "../pages/user/CompanyPage";
import { Device } from "../pages/new/DeviceSelection";
import { Location } from "../pages/new/LocationSelection";

// ==========================================
// CLIENT HOOKS
// ==========================================

export function useAddClient(): UseMutationResult<
  CustomResponse<any>,
  Error,
  ClientModel
> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ["clients", "create"],
    mutationFn: addClient,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["clients"] });
    },
    onError: (error) => {
      toast.error(handleApiError(error));
    },
  });
}

export function useEditClient(): UseMutationResult<
  CustomResponse<any>,
  Error,
  ClientModel
> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ["clients", "update"],
    mutationFn: editClient,
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: ["clients", "all"] });
      if (variables.id) {
        queryClient.invalidateQueries({
          queryKey: ["clients", "detail", variables.id],
        });
      }
    },
    onError: (error) => {
      toast.error(handleApiError(error));
    },
  });
}

export function useDeleteClient(): UseMutationResult<
  CustomResponse<any>,
  Error,
  string
> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ["clients", "delete"],
    mutationFn: deleteClient,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["clients", "all"] });
      toast.success("Client deleted successfully!");
    },
    onError: (error) => {
      toast.error(handleApiError(error));
    },
  });
}

export function useGetAllClients(): UseQueryResult<ClientDetailModel[], Error> {
  return useQuery({
    queryKey: ["clients", "all"],
    queryFn: getAllClients,
    staleTime: 1000 * 60 * 5, // 5 minutes
    gcTime: 1000 * 60 * 10, // 10 minutes
  });
}

export function useGetClientsDetail(
  id: string,
): UseQueryResult<ICompanyModel, Error> {
  return useQuery({
    queryKey: ["clients", "detail", id],
    queryFn: getClientDetail,
    enabled: !!id,
    staleTime: 1000 * 60 * 5, // 5 minutes
    gcTime: 1000 * 60 * 10, // 10 minutes
  });
}

export function useEditGasMappingDetails(): UseMutationResult<
  CustomResponse<string>,
  Error,
  any
> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ["clients", "gas-mapping"],
    mutationFn: editGasMapping,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["clients"] });
      toast.success("Gas mapping updated successfully!");
    },
    onError: (error) => {
      toast.error(handleApiError(error));
    },
  });
}

// ==========================================
// DEVICE HOOKS
// ==========================================

export function useCreateDevice(): UseMutationResult<
  CustomResponse<any>,
  Error,
  any
> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ["devices", "create"],
    mutationFn: createDevice,
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: ["devices", "all"] });
      if (variables.locationId) {
        queryClient.invalidateQueries({
          queryKey: ["devices", "location", variables.locationId],
        });
      }
    },
    onError: (error) => {
      toast.error(handleApiError(error));
    },
  });
}

export function useGetAllDevices(): UseQueryResult<any[], Error> {
  return useQuery({
    queryKey: ["devices", "all"],
    queryFn: getAllDevices,
    staleTime: 0, // isOnline is time-sensitive; always refetch on mount/focus
    gcTime: 1000 * 60 * 10, // 10 minutes
    refetchInterval: 30000, // Keep online/offline status continuously up to date
  });
}

export function useGetUserDevices(): UseQueryResult<any, Error> {
  return useQuery({
    queryKey: ["devices", "user"],
    queryFn: getUserDevices,
    staleTime: 1000 * 60 * 2, // 2 minutes
    gcTime: 1000 * 60 * 5, // 5 minutes
  });
}

export function useGetDeviceRange(
  id: string,
): UseQueryResult<IWeatherDataRange, Error> {
  return useQuery({
    queryKey: ["devices", "range", id],
    queryFn: getDevicesRange,
    enabled: !!id,
    staleTime: 1000 * 60 * 5, // 5 minutes
    gcTime: 1000 * 60 * 10, // 10 minutes
  });
}

export function useGetDevicesForLocation(
  locationId: string | null,
): UseQueryResult<Device[], Error> {
  return useQuery({
    queryKey: ["get-location-devices", locationId],
    queryFn: getDevicesForLocation,
    enabled: !!locationId,
    staleTime: 1000 * 60 * 5, // 5 minutes
    gcTime: 1000 * 60 * 10, // 10 minutes
  });
}

export function useUpdateDeviceRange(): UseMutationResult<
  CustomResponse<string>,
  Error,
  IWeatherDataRange
> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ["devices", "update-range"],
    mutationFn: updateDeviceRange,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["devices", "range"] });
      toast.success("Device range updated successfully!");
    },
    onError: (error) => {
      toast.error(handleApiError(error));
    },
  });
}

export function useUpdateDevice(): UseMutationResult<
  CustomResponse<any>,
  Error,
  { deviceId: string; deviceData: any }
> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ["devices", "update"],
    mutationFn: ({ deviceId, deviceData }) =>
      updateDevice(deviceId, deviceData),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["devices"] });
      toast.success("Device updated successfully!");
    },
    onError: (error) => {
      toast.error(handleApiError(error));
    },
  });
}

export function useDeleteDevice(): UseMutationResult<
  CustomResponse<any>,
  Error,
  string
> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ["devices", "delete"],
    mutationFn: deleteDevice,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["devices"] });
    },
    onError: (error) => {
      toast.error(handleApiError(error));
    },
  });
}

export function useRegisterDevice(): UseMutationResult<
  CustomResponse<any>,
  Error,
  RegisterDeviceDto
> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ["devices", "register"],
    mutationFn: registerDevice,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["devices"] });
    },
    onError: (error) => {
      toast.error(handleApiError(error));
    },
  });
}

export function useConnectDeviceWithClient(): UseMutationResult<
  CustomResponse<any>,
  Error,
  ConnectDeviceModel
> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ["devices", "connect-client"],
    mutationFn: connectDeviceWithClient,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["devices"] });
      queryClient.invalidateQueries({ queryKey: ["clients"] });
      toast.success("Device connected to client successfully!");
    },
    onError: (error) => {
      toast.error(handleApiError(error));
    },
  });
}

export function useChangeDeviceState(): UseMutationResult<
  CustomResponse<any>,
  Error,
  ChangeDeviceModel
> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ["devices", "change-state"],
    mutationFn: changeDeviceState,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["devices"] });
      toast.success("Device state changed successfully!");
    },
    onError: (error) => {
      toast.error(handleApiError(error));
    },
  });
}

// Legacy alias for backwards compatibility
export function useAddDeviceToClient() {
  return useConnectDeviceWithClient();
}

// ==========================================
// LOCATION HOOKS
// ==========================================

export function useCreateLocation(): UseMutationResult<
  CustomResponse<any>,
  Error,
  any
> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ["locations", "create"],
    mutationFn: createLocation,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["locations", "all"] });
    },
    onError: (error) => {
      toast.error(handleApiError(error));
    },
  });
}

export function useGetAllLocations(): UseQueryResult<any[], Error> {
  return useQuery({
    queryKey: ["locations", "all"],
    queryFn: getAllLocations,
    staleTime: 1000 * 60 * 5, // 5 minutes
    gcTime: 1000 * 60 * 10, // 10 minutes
  });
}

export function useGetLocation(id: string): UseQueryResult<Location, Error> {
  return useQuery({
    queryKey: ["locations", "detail", id],
    queryFn: getLocation,
    enabled: !!id,
    staleTime: 1000 * 60 * 5, // 5 minutes
    gcTime: 1000 * 60 * 10, // 10 minutes
  });
}

export function useUpdateLocation(): UseMutationResult<
  CustomResponse<any>,
  Error,
  any
> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ["locations", "update"],
    mutationFn: updateLocation,
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: ["locations", "all"] });
      if (variables.id) {
        queryClient.invalidateQueries({
          queryKey: ["locations", "detail", variables.id],
        });
      }
    },
    onError: (error) => {
      toast.error(handleApiError(error));
    },
  });
}

export function useDeleteLocation(): UseMutationResult<
  CustomResponse<any>,
  Error,
  string
> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ["locations", "delete"],
    mutationFn: deleteLocation,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["locations"] });
    },
    onError: (error) => {
      toast.error(handleApiError(error));
    },
  });
}

// ==========================================
// USER MANAGEMENT HOOKS
// ==========================================

export function useGetAllUsers(): UseQueryResult<any[], Error> {
  return useQuery({
    queryKey: ["users", "all"],
    queryFn: getAllUsers,
    staleTime: 1000 * 60 * 2,
    gcTime: 1000 * 60 * 5,
  });
}

export function useSignUpUser(): UseMutationResult<
  CustomResponse<any>,
  Error,
  { email: string; name: string; password?: string; role?: "ADMIN" | "USER" }
> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ["users", "signUp"],
    mutationFn: signUpUser,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["users", "all"] });
      toast.success("Member added successfully!");
    },
    onError: (error) => {
      toast.error(handleApiError(error));
    },
  });
}

export function useDeleteUser(): UseMutationResult<
  CustomResponse<any>,
  Error,
  string
> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ["users", "delete"],
    mutationFn: deleteUser,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["users", "all"] });
      toast.success("User deleted successfully!");
    },
    onError: (error) => {
      toast.error(handleApiError(error));
    },
  });
}

export function useDeactivateUser(): UseMutationResult<
  CustomResponse<any>,
  Error,
  { email: string; deactivated: boolean }
> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ["users", "deactivate"],
    mutationFn: ({ email, deactivated }) => deactivateUser(email, deactivated),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["users", "all"] });
    },
    onError: (error) => {
      toast.error(handleApiError(error));
    },
  });
}

export function useGetUserPreference(): UseQueryResult<IPreference, Error> {
  return useQuery({
    queryKey: ["users", "preferences"],
    queryFn: getUserPreference,
    staleTime: 1000 * 60 * 5, // 5 minutes
    gcTime: 1000 * 60 * 10, // 10 minutes
  });
}

export function useUpdateUserPreference(): UseMutationResult<
  CustomResponse<string>,
  Error,
  IPreference
> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ["users", "update-preferences"],
    mutationFn: updatePreference,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["users", "preferences"] });
      toast.success("User preferences updated successfully!");
    },
    onError: (error) => {
      toast.error(handleApiError(error));
    },
  });
}

export function useGenerateCredentials(): UseMutationResult<
  CustomResponse<any>,
  Error,
  string
> {
  return useMutation({
    mutationKey: ["users", "generate-credentials"],
    mutationFn: generateCredentials,
    onSuccess: () => {
      toast.success("Credentials generated successfully!");
    },
    onError: (error) => {
      toast.error(handleApiError(error));
    },
  });
}

export function useUpdateProfile(): UseMutationResult<
  CustomResponse<any>,
  Error,
  {
    name?: string;
    phone?: string;
    address?: string;
    gstNo?: string;
    profileImage?: string;
    organisationName?: string;
  }
> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ["users", "update-profile"],
    mutationFn: updateProfile,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["devices", "user"] });
      toast.success("Profile updated successfully!");
    },
    onError: (error) => {
      toast.error(handleApiError(error));
    },
  });
}

export function useUpdateUserRole(): UseMutationResult<
  CustomResponse<any>,
  Error,
  { id: string; role: string }
> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ["users", "update-role"],
    mutationFn: ({ id, role }) => updateUserRole(id, role),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["users", "all"] });
      queryClient.invalidateQueries({ queryKey: ["clients"] });
      toast.success("User role updated successfully!");
    },
    onError: (error) => {
      toast.error(handleApiError(error));
    },
  });
}

// ==========================================
// ORGANIZATION ASSIGNMENT HOOKS
// ==========================================

export function useGetUnassignedUsers(): UseQueryResult<any[], Error> {
  return useQuery({
    queryKey: ["users", "unassigned"],
    queryFn: getUnassignedUsers,
    staleTime: 1000 * 60 * 2,
    gcTime: 1000 * 60 * 5,
  });
}

export function useAssignUserToClient(): UseMutationResult<
  CustomResponse<any>,
  Error,
  { userId: string; clientId: string }
> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ["users", "assign-client"],
    mutationFn: ({ userId, clientId }) => assignUserToClient(userId, clientId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["users"] });
      queryClient.invalidateQueries({ queryKey: ["clients"] });
      toast.success("User assigned to organization successfully!");
    },
    onError: (error) => {
      toast.error(handleApiError(error));
    },
  });
}

export function useSwitchUserClient(): UseMutationResult<
  CustomResponse<any>,
  Error,
  string
> {
  // No invalidate/toast on success: the caller swaps auth and reloads the
  // page; invalidating here refetches every query with the old org's token.
  return useMutation({
    mutationKey: ["users", "switch-client"],
    mutationFn: switchUserClient,
    onError: (error) => {
      toast.error(handleApiError(error));
    },
  });
}

export function useGetAvailableClients(): UseQueryResult<
  { id: string; name: string; logo: string }[],
  Error
> {
  return useQuery({
    queryKey: ["clients", "available"],
    queryFn: getAvailableClients,
    staleTime: 1000 * 60 * 5,
    gcTime: 1000 * 60 * 10,
  });
}

export function useOnboardOrganization(): UseMutationResult<
  CustomResponse<any>,
  Error,
  { organizationName?: string; clientId?: string }
> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ["users", "onboard-org"],
    mutationFn: onboardOrganization,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["clients"] });
      queryClient.invalidateQueries({ queryKey: ["billing"] });
    },
    onError: (error) => {
      toast.error(handleApiError(error));
    },
  });
}

// ==========================================
// WEATHER DATA HOOKS
// ==========================================

export function useWeatherData(
  deviceId: string,
  metric: string,
  date: TimePeriod,
  timeGap: TimeGap
): UseQueryResult<IWeatherData[], Error> {
  const day = parseFloat(date.split(" Day")[0]);
  const ms = getMsFromTimeGap(timeGap);
  return useQuery({
    queryKey: ["weather", "data", deviceId, metric, day, ms],
    queryFn: getWeatherData,
    enabled: !!deviceId && !!metric,
    staleTime: 1000 * 60 * 2, // 2 minutes for weather data
    gcTime: 1000 * 60 * 5, // 5 minutes
    retry: 3,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
  });
}

export function useGetLiveData(
  deviceId?: string,
): UseQueryResult<IWeatherData, Error> {
  return useQuery({
    queryKey: ["weather", "live", deviceId],
    queryFn: getLiveWeatherData,
    enabled: !!deviceId,
    refetchInterval: 30000, // Refetch every 30 seconds for live data
    staleTime: 0, // Always consider live data stale
    gcTime: 1000 * 60 * 2, // Keep in cache for 2 minutes
  });
}

/**
 * Last known reading per device, straight from the DB. Widgets that render
 * live values (grid cards, parameter table) use this as the value shown
 * before MQTT delivers its first message on a given page load — so a
 * returning user with historical data sees that data instead of zeros, and
 * only a device with no data at all (e.g. never connected) reads as empty.
 */
export function useGetMultiDeviceLiveData(
  deviceIds: string[],
): Record<string, IWeatherData | null> {
  const results = useQueries({
    queries: deviceIds.map((id) => ({
      queryKey: ["weather", "live", id] as const,
      queryFn: getLiveWeatherData,
      enabled: !!id,
      refetchInterval: 30000,
      staleTime: 0,
      gcTime: 1000 * 60 * 2,
    })),
  });

  const map: Record<string, IWeatherData | null> = {};
  deviceIds.forEach((id, i) => {
    map[id] = results[i]?.data ?? null;
  });
  return map;
}

export function useGenerateReport(): UseMutationResult<
  string,
  Error,
  ReportRequestDto
> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ["weather", "generate-report"],
    mutationFn: generateReport,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["weather", "reports"] });
    },
    onError: (error) => {
      toast.error(handleApiError(error));
    },
  });
}

export function useGetReports(): UseQueryResult<ReportItem[], Error> {
  return useQuery({
    queryKey: ["weather", "reports"],
    queryFn: getReports,
    staleTime: 1000 * 60 * 2,
    gcTime: 1000 * 60 * 5,
  });
}

export function useDownloadReport(): UseMutationResult<void, Error, { jobId: string; format?: "xlsx" | "pdf" }> {
  return useMutation({
    mutationKey: ["weather", "download-report"],
    mutationFn: ({ jobId, format }) => downloadReport(jobId, format),
    onSuccess: () => {
      toast.success("Report downloaded successfully!");
    },
    onError: (error) => {
      toast.error(handleApiError(error));
    },
  });
}

export function useDeleteReport(): UseMutationResult<string, Error, string> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ["weather", "delete-report"],
    mutationFn: deleteReport,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["weather", "reports"] });
      toast.success("Report deleted successfully!");
    },
    onError: (error) => {
      toast.error(handleApiError(error));
    },
  });
}

// ==========================================
// PARAMETER HOOKS
// ==========================================

export function useCreateParameter(): UseMutationResult<
  CustomResponse<any>,
  Error,
  ParameterCreateDto
> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ["parameters", "create"],
    mutationFn: createParameter,
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({
        queryKey: ["parameters", "device", variables.deviceId],
      });
      toast.success("Parameter created successfully!");
    },
    onError: (error) => {
      toast.error(handleApiError(error));
    },
  });
}

export function useGetDeviceParameters(
  deviceId: string,
): UseQueryResult<any[], Error> {
  return useQuery({
    queryKey: ["parameters", "device", deviceId],
    queryFn: getDeviceParameters,
    enabled: !!deviceId,
    staleTime: 1000 * 60 * 5, // 5 minutes
    gcTime: 1000 * 60 * 10, // 10 minutes
  });
}

export function useUpdateParameter(): UseMutationResult<
  CustomResponse<any>,
  Error,
  { parameterId: string; data: ParameterUpdateDto }
> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ["parameters", "update"],
    mutationFn: ({ parameterId, data }) => updateParameter(parameterId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["parameters"] });
      toast.success("Parameter updated successfully!");
    },
    onError: (error) => {
      toast.error(handleApiError(error));
    },
  });
}

export function useDeleteParameter(): UseMutationResult<
  CustomResponse<any>,
  Error,
  string
> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ["parameters", "delete"],
    mutationFn: deleteParameter,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["parameters"] });
      toast.success("Parameter deleted successfully!");
    },
    onError: (error) => {
      toast.error(handleApiError(error));
    },
  });
}

// ==========================================
// UTILITY HOOKS
// ==========================================

export function useGetMessage(): UseQueryResult<any, Error> {
  return useQuery({
    queryKey: ["messages", "user"],
    queryFn: getMessage,
    staleTime: 1000 * 60 * 2, // 2 minutes
    gcTime: 1000 * 60 * 5, // 5 minutes
  });
}

export function useSendEmail(): UseMutationResult<
  CustomResponse<any>,
  Error,
  SendEmailDto
> {
  return useMutation({
    mutationKey: ["email", "send"],
    mutationFn: sendEmail,
    onSuccess: () => {
      toast.success("Email sent successfully!");
    },
    onError: (error) => {
      toast.error(handleApiError(error));
    },
  });
}

// ==========================================
// COMPLIANCE HOOKS
// ==========================================

import type {
  ComplianceProfile,
  ComplianceProfileCreateDto,
  ComplianceProfileUpdateDto,
  ComplianceCheckResult,
  SlidingWindowDays,
} from "../types/compliance";

export function useGetAllComplianceProfiles(): UseQueryResult<ComplianceProfile[], Error> {
  return useQuery({
    queryKey: ["compliance", "all"],
    queryFn: getAllComplianceProfiles,
    staleTime: 1000 * 60 * 2,
    gcTime: 1000 * 60 * 5,
  });
}

export function useCreateComplianceProfile(): UseMutationResult<
  ComplianceProfile,
  Error,
  ComplianceProfileCreateDto
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ["compliance", "create"],
    mutationFn: createComplianceProfile,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["compliance"] });
      toast.success("Compliance profile created!");
    },
    onError: (error) => {
      toast.error(handleApiError(error));
    },
  });
}

export function useUpdateComplianceProfile(): UseMutationResult<
  ComplianceProfile,
  Error,
  { id: string; data: ComplianceProfileUpdateDto }
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ["compliance", "update"],
    mutationFn: ({ id, data }) => updateComplianceProfile(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["compliance"] });
      toast.success("Compliance profile updated!");
    },
    onError: (error) => {
      toast.error(handleApiError(error));
    },
  });
}

export function useDeleteComplianceProfile(): UseMutationResult<void, Error, string> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ["compliance", "delete"],
    mutationFn: deleteComplianceProfile,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["compliance"] });
      toast.success("Compliance profile deleted!");
    },
    onError: (error) => {
      toast.error(handleApiError(error));
    },
  });
}

export function useCheckCompliance(
  profileId: string | null,
  days: SlidingWindowDays
): UseQueryResult<ComplianceCheckResult, Error> {
  return useQuery({
    queryKey: ["compliance", "check", profileId, days],
    queryFn: checkCompliance,
    enabled: !!profileId,
    staleTime: 1000 * 60 * 2,
    gcTime: 1000 * 60 * 5,
  });
}

export function useDownloadComplianceReport(): UseMutationResult<
  void,
  Error,
  { profileId: string; days: number }
> {
  return useMutation({
    mutationKey: ["compliance", "download-report"],
    mutationFn: ({ profileId, days }) => downloadComplianceReport(profileId, days),
    onSuccess: () => {
      toast.success("Compliance report downloaded!");
    },
    onError: (error) => {
      toast.error(handleApiError(error));
    },
  });
}
